import React, { CSSProperties, useCallback, useEffect, useState } from 'react'
import { View } from 'react-native'
import { WebView, WebViewMessageEvent } from 'react-native-webview'
import { useTheme } from 'styled-components/native'

import { useDispatch } from 'APP/Hooks/redux'
import ActivityIndicator from 'APP/Components/ActivityIndicator'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { mediaPlaybackStarted, mediaPlaybackStopped } from 'APP/Redux/MediaRedux'
import { mediaTrackingScript } from 'APP/Lib/WebView/mediaTrackingScript'

import Styles from './style'

type WebViewNativeMessage = WebViewMessageEvent['nativeEvent']

export interface DialogueWebViewProps {
  uri: string
  title?: string
  headers?: Record<string, string>
  userSelect?: CSSProperties['userSelect']
  incognito?: boolean
  originWhitelist?: string[]
  route?: { name?: string; params?: Record<string, unknown> }
  onEvent?: (event: MessageEvent | WebViewNativeMessage) => void
}

const DialogueWebView = (props: DialogueWebViewProps) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const { uri, title, headers, userSelect, incognito, originWhitelist, route, onEvent } = props

  const [loading, setLoading] = useState(true)

  const onLoadEnd = () => {
    setTimeout(() => {
      setLoading(false)
    }, 250)
  }

  const onLoadStart = () => setLoading(true)

  const renderLoadingOverlay = () => {
    return (
      <View testID="DIALOGUEWEBVIEW_LOADING_CONTAINER" style={Styles.loadingOverlay}>
        <ActivityIndicator color={theme.colors.secondary} />
      </View>
    )
  }

  const onWebViewMessage = useCallback(
    (event: MessageEvent | WebViewMessageEvent) => {
      const eventData = isWeb()
        ? (event as MessageEvent)?.data
        : (event as WebViewMessageEvent)?.nativeEvent?.data

      if (eventData) {
        try {
          const data = JSON.parse(eventData)

          if (data.type === 'MEDIA_PLAYBACK_STARTED') {
            dispatch(mediaPlaybackStarted())
            if (onEvent) {
              isWeb()
                ? onEvent(event as MessageEvent)
                : onEvent((event as WebViewMessageEvent).nativeEvent)
            }
            return
          }

          if (data.type === 'MEDIA_PLAYBACK_STOPPED') {
            dispatch(mediaPlaybackStopped())
            if (onEvent) {
              isWeb()
                ? onEvent(event as MessageEvent)
                : onEvent((event as WebViewMessageEvent).nativeEvent)
            }
            return
          }
        } catch (_error) {
          // If JSON parsing fails, continue to pass event to parent
        }
      }

      if (isWeb() && event && onEvent) {
        onEvent(event as MessageEvent)
      } else if ((event as WebViewMessageEvent).nativeEvent && onEvent) {
        onEvent((event as WebViewMessageEvent).nativeEvent)
      }
    },
    [dispatch, onEvent]
  )

  useEffect(() => {
    if (isWeb()) {
      window.addEventListener('message', onWebViewMessage as EventListener)

      return () => {
        window.removeEventListener('message', onWebViewMessage as EventListener)
      }
    }
  }, [])

  const injectedJavaScript = `
    var node = document.createElement('style');
    var rules = ""
    rules += "html, body { background: ${theme.colors.background}; }";
    rules += "body {  color: ${theme.colors.text}; margin: 24px; }";
    rules += "a { color: ${theme.colors.accent} }"
    node.innerHTML = rules;
    document.querySelector('HEAD').appendChild(node);

    ${mediaTrackingScript}
  `

  // Promoted Content links will always be in light mode (DIA-38333)
  // TODO: Change this to be flag-driven, something like "injectStyling:false".
  // In order for `injectedJavaScript` to work on web (iframe), additional work will be needed. Bye dark mode, for now.
  const injected = route?.name === 'promotedContent' ? '' : injectedJavaScript

  return (
    <>
      {isWeb() ? (
        <iframe
          title={title}
          style={{
            borderWidth: '0',
            padding: '2rem',
            userSelect: userSelect || 'all',
          }}
          src={uri}
          height={'100%'}
          onLoad={onLoadEnd}
        />
      ) : (
        <WebView
          style={Styles.webView}
          injectedJavaScript={injected}
          javaScriptEnabledAndroid={true}
          source={{ uri, headers }}
          scalesPageToFit={false}
          onLoadEnd={onLoadEnd}
          onLoadStart={onLoadStart}
          allowsInlineMediaPlayback
          onMessage={onWebViewMessage}
          allowsFullscreenVideo={true}
          incognito={incognito}
          originWhitelist={originWhitelist}
        />
      )}
      {loading && renderLoadingOverlay()}
    </>
  )
}

export default DialogueWebView
